import React, { useCallback, useEffect, useState } from 'react';
import { getOperationalIssueSettings, updateOperationalIssueSettings } from '../../../services/operationalIssues';
import { describeHttpError } from '../../../utils/apiError';
import { pluralDays } from './issueConstants';

/**
 * ТЗ №2 §3.1: admin modal «Период анализа Operational Issues».
 * desired (window_days) ≠ applied (applied_window_days); pending — норма,
 * не ошибка; response PUT — единственная истина. Без polling.
 */
const IssueSettingsModal = ({ onClose, onApplied }) => {
  const [settings, setSettings] = useState(null);
  const [value, setValue] = useState('');
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null); // { tone: 'success'|'warning'|'error', text }

  const load = useCallback(() => getOperationalIssueSettings()
    .then((data) => {
      setSettings(data);
      setValue(String(data.window_days));
      setLoadError('');
    })
    .catch((error) => setLoadError(describeHttpError(error, 'Не удалось загрузить настройки'))), []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const n = Number(value);
  const inRange = Boolean(settings) && Number.isInteger(n)
    && n >= settings.min_window_days && n <= settings.max_window_days;

  const save = () => {
    if (saving || !inRange) return;
    setSaving(true);
    setNotice(null);
    updateOperationalIssueSettings({ window_days: n })
      .then((data) => {
        setSettings(data);
        setValue(String(data.window_days));
        setNotice(data.pending
          ? { tone: 'warning', text: `Настройка сохранена. Пересчёт ещё не применён. Сейчас используется ${pluralDays(data.applied_window_days)}` }
          : { tone: 'success', text: `Период ${pluralDays(data.applied_window_days)} применён` });
        onApplied();
      })
      .catch((error) => {
        if (error?.response?.status === 422) {
          setNotice({ tone: 'error', text: describeHttpError(error, 'Некорректное значение') });
          return;
        }
        // ТЗ №2 §11: при network error не предполагать, что значение не
        // сохранилось — перечитать GET перед повторной записью.
        setNotice({ tone: 'error', text: 'Не удалось подтвердить сохранение. Настройки перечитаны.' });
        load();
      })
      .finally(() => setSaving(false));
  };

  return (
    <div className="oi-modal-backdrop" onClick={onClose}>
      <div
        className="oi-modal"
        role="dialog"
        aria-label="Период анализа Operational Issues"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="oi-modal-title">Период анализа Operational Issues</h2>

        {loadError && (
          <p className="oi-error" role="alert">
            {loadError}{' '}
            <button type="button" className="sum-btn" onClick={load}>Повторить</button>
          </p>
        )}

        {settings && (
          <>
            <label className="sum-field">
              <span className="sum-field-label">Период анализа, дней</span>
              <input
                className="oi-input"
                type="number"
                min={settings.min_window_days}
                max={settings.max_window_days}
                step={1}
                value={value}
                onChange={(event) => setValue(event.target.value)}
              />
            </label>
            {!(Number.isInteger(n) && n > 0) && (
              <p className="sum-muted">
                {`Допустимо от ${settings.min_window_days} до ${settings.max_window_days} дней`}
              </p>
            )}
            {!inRange && value !== '' && (
              <p className="sum-field-error">Допустимо от {settings.min_window_days} до {settings.max_window_days} дней</p>
            )}
            <p className="oi-applied">
              Сейчас {pluralDays(settings.applied_window_days)}
              {settings.pending ? ` · ожидает пересчёта до ${pluralDays(settings.window_days)}` : ''}
            </p>
            {notice && (
              <p className={`oi-notice oi-notice--${notice.tone}`} role={notice.tone === 'error' ? 'alert' : 'status'}>
                {notice.text}
              </p>
            )}
            <div className="oi-modal-actions">
              <button type="button" className="sum-btn sum-btn--primary" disabled={saving || !inRange} onClick={save}>
                Сохранить
              </button>
              <button type="button" className="sum-btn" onClick={onClose}>Закрыть</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default IssueSettingsModal;
